import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, Instagram, Facebook, Linkedin, ArrowUpRight, CalendarCheck } from "lucide-react";
import { useOrg } from "@/contexts/OrgContext";
import { useCompanyInfoFull, type SocialLinks, type CompanyInfoFull } from "@/hooks/useCompanyInfo";
import { isAwaitingFirstResult, useTenantOrgId } from "@/hooks/useTenantOrgId";
import { Skel } from "@/components/home/TenantSkeletons";
import { TenantImage } from "@/components/home/TenantImage";
import demoFallbackLogo from "@/assets/demo-fallback-logo.png";
import logo from "@/assets/logo.png";
import placeholderLogo from "@/assets/logo-placeholder.webp";
import { DEMO_BUSINESS_NAME } from "@/config/demoShowcase";
import { useThemeVariant } from "@/contexts/ThemeContext";
import { useShowsPoweredBy } from "@/hooks/useShowsPoweredBy";

type SocialLinkValue = string | null | undefined | {
  enabled?: boolean;
  url?: string | null;
};

type CompanyInfo = CompanyInfoFull;

function defaultTagline(areas: string[]): string {
  if (!areas || areas.length === 0) return "Professional cleaning services in your area.";
  if (areas.length === 1) return `Professional cleaning services across ${areas[0]}.`;
  return `Professional cleaning services across ${areas.slice(0, 2).join(" & ")}.`;
}

const POLISHED_DEFAULT: CompanyInfo = {
  company_name: "The Polished Version",
  logo_url: null,
  footer_tagline: "Professional cleaning services across Cardiff & Newport. Upgrade your clean to the polished version.",
  phone_number: null,
  service_areas: ["Cardiff", "Newport"],
  social_links: null,
};

/** While the tenant resolves: no name, no areas — never another business's details. */
const PENDING_INFO: CompanyInfo = {
  company_name: "",
  logo_url: null,
  footer_tagline: null,
  phone_number: null,
  service_areas: [],
  social_links: null,
};

function SocialIcon({
  href,
  label,
  children,
}: {
  href: string | null | undefined;
  label: string;
  children: React.ReactNode;
}) {
  const cls = "tp-footer-social";
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} aria-label={label}>
        {children}
      </a>
    );
  }
  return (
    <span className={cls + " cursor-default opacity-50"} aria-label={label}>
      {children}
    </span>
  );
}

function getSocialHref(value: SocialLinkValue): string | null {
  if (!value) return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (value.enabled === false) return null;

  const trimmed = value.url?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}

export function Footer() {
  const { isMarketingSite } = useOrg();
  const orgId = useTenantOrgId();
  const { pathname } = useLocation();
  const variant = useThemeVariant();
  const isDemoRoute = pathname === "/demo" || pathname.startsWith("/demo/");

  const isPendingQuery = useCompanyInfoFull(orgId);
  const { data: fetchedInfo } = isPendingQuery;
  const isPending = isAwaitingFirstResult(isPendingQuery);
  const companyInfo: CompanyInfo = fetchedInfo ?? (isPending ? PENDING_INFO : POLISHED_DEFAULT);

  // The demo shows prospects where their own logo and business name go.
  const logoSrc = isDemoRoute
    ? placeholderLogo
    : isMarketingSite
    ? companyInfo.logo_url || logo
    : companyInfo.logo_url || demoFallbackLogo;

  const tagline =
    companyInfo.footer_tagline ||
    defaultTagline(companyInfo.service_areas);

  const businessName = isDemoRoute ? DEMO_BUSINESS_NAME : companyInfo.company_name;
  const social = companyInfo.social_links ?? {};
  const year = new Date().getFullYear();
  // Solo and Team always show the credit; Business can switch it off.
  const poweredByQuery = useShowsPoweredBy(isMarketingSite ? null : orgId);
  const showPoweredBy = !isMarketingSite && poweredByQuery.data === true;

  const link = (path: string) => (isDemoRoute ? (path === "/" ? "/demo" : `/demo${path}`) : path);

  const quickLinks = [
    { name: "About Us", href: link("/about") },
    { name: "Our Services", href: link("/services") },
    { name: "Book Online", href: link("/booking") },
    { name: "Contact Us", href: link("/contact") },
  ];

  const logoNode = isPending ? (
    <Skel className="tp-skeleton h-10 w-40 rounded-xl" />
  ) : (
    <TenantImage
      src={logoSrc}
      alt={isDemoRoute ? "Your logo here" : companyInfo.company_name}
      className="tp-footer-logo h-[76px] object-contain"
    />
  );

  const taglineNode = isPending ? (
    <div className="tp-skeleton max-w-sm space-y-2.5 py-1" aria-hidden>
      <Skel className="h-3.5 w-full tp-skel-round" />
      <Skel className="h-3.5 w-3/4 tp-skel-round" />
    </div>
  ) : (
    <p className="tp-footer-muted max-w-sm text-[0.95rem] leading-relaxed">{tagline}</p>
  );

  const bookButton = (
    <Link
      to={link("/booking")}
      className="group inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
    >
      <CalendarCheck className="h-4 w-4" />
      Book online
      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
    </Link>
  );

  const socials = (
    <div className="flex gap-2.5">
      <SocialIcon href={getSocialHref(social.instagram)} label="Instagram"><Instagram className="h-4 w-4" /></SocialIcon>
      <SocialIcon href={getSocialHref(social.facebook)} label="Facebook"><Facebook className="h-4 w-4" /></SocialIcon>
      <SocialIcon href={getSocialHref(social.linkedin)} label="LinkedIn"><Linkedin className="h-4 w-4" /></SocialIcon>
    </div>
  );

  const legalLinks = (
    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
      <Link to="/privacy" className="tp-footer-muted transition-colors hover:text-[hsl(var(--tp-footer-fg))]">Privacy Policy</Link>
      <Link to="/terms" className="tp-footer-muted transition-colors hover:text-[hsl(var(--tp-footer-fg))]">Terms & Conditions</Link>
      <Link to="/cookies" className="tp-footer-muted transition-colors hover:text-[hsl(var(--tp-footer-fg))]">Cookie Policy</Link>
      <Link to="/auth" className="tp-footer-muted transition-colors hover:text-[hsl(var(--tp-footer-fg))]">Admin</Link>
    </div>
  );

  // "Powered by" sits on the same line, straight after "All rights reserved."
  const copyright = (
    <p className="tp-footer-muted text-sm">
      © {year} {businessName}. All rights reserved.
      {showPoweredBy && (
        <>
          {" "}Powered by{" "}
          <a href="https://thepolishedversion.com" target="_blank" rel="noopener" className="tp-footer-link underline underline-offset-2">
            The Polished Version
          </a>
        </>
      )}
    </p>
  );

  // Minimal: one quiet row.
  if (variant === "modern-minimal") {
    return (
      <footer className="tp-footer relative z-10" data-footer="compact">
        <div className="container relative mx-auto px-5 py-10 md:px-6 md:py-12">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <Link to={link("/")} className="inline-flex h-[60px] items-center">{logoNode}</Link>
            <nav aria-label="Footer" className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
              {quickLinks.map((item) => (<Link key={item.name} to={item.href} className="tp-footer-link">{item.name}</Link>))}
              {companyInfo.phone_number && (
                <a href={`tel:${companyInfo.phone_number.replace(/\s/g, '')}`} className="tp-footer-link">{companyInfo.phone_number}</a>
              )}
            </nav>
            {socials}
          </div>
          <div className="tp-footer-rule mt-8 flex flex-col gap-4 border-t pt-6 md:flex-row md:items-center md:justify-between">
            {copyright}
            {legalLinks}
          </div>
          {companyInfo.service_areas.length > 0 && !isPending && (
            <p className="tp-footer-muted mt-4 text-xs uppercase tracking-[0.18em]">Serving {companyInfo.service_areas.join(" · ")}</p>
          )}
        </div>
      </footer>
    );
  }

  // Elegant: centred masthead.
  if (variant === "elegant-serif") {
    return (
      <footer className="tp-footer relative z-10" data-footer="centered">
        <div className="container relative mx-auto px-5 pb-10 pt-16 text-center md:px-6 md:pt-20">
          <Link to={link("/")} className="inline-flex h-[76px] items-center justify-center">{logoNode}</Link>
          <div className="mx-auto mt-5 flex max-w-md justify-center [&_p]:text-center">{taglineNode}</div>
          <div className="mx-auto my-8 flex max-w-xs items-center gap-4 opacity-60" aria-hidden>
            <span className="h-px flex-1 bg-current" /><span className="h-1.5 w-1.5 rotate-45 border border-current" /><span className="h-px flex-1 bg-current" />
          </div>
          <nav aria-label="Footer" className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-xs font-semibold uppercase tracking-[0.24em]">
            {quickLinks.map((item) => (<Link key={item.name} to={item.href} className="tp-footer-link">{item.name}</Link>))}
          </nav>
          {companyInfo.service_areas.length > 0 && !isPending && (
            <p className="tp-footer-muted mt-8 text-sm italic">Serving {companyInfo.service_areas.join(", ")}</p>
          )}
          <div className="mt-8 flex flex-col items-center gap-5">
            {companyInfo.phone_number && (
              <a href={`tel:${companyInfo.phone_number.replace(/\s/g, '')}`} className="tp-footer-link text-lg">{companyInfo.phone_number}</a>
            )}
            {socials}
          </div>
          <div className="tp-footer-rule mt-12 flex flex-col items-center gap-4 border-t pt-8">
            {copyright}
            {legalLinks}
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="tp-footer relative z-10" data-footer="columns">
      <div className="container relative mx-auto px-5 pb-10 pt-16 md:px-6 md:pb-12 md:pt-24">
        <div className="grid grid-cols-2 gap-x-6 gap-y-12 md:grid-cols-3 lg:grid-cols-12 lg:gap-8">

          {/* Brand */}
          <div className="col-span-2 space-y-6 md:col-span-3 lg:col-span-5 lg:pr-12">
            <Link to={link("/")} className="inline-flex h-[76px] items-center">{logoNode}</Link>
            {taglineNode}
            {bookButton}
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h3 className="tp-footer-heading mb-5">Company</h3>
            <ul className="space-y-3">
              {quickLinks.map((item) => (
                <li key={item.name}>
                  <Link to={item.href} className="tp-footer-link text-[0.95rem]">{item.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Areas */}
          <div className="lg:col-span-2">
            <h3 className="tp-footer-heading mb-5">Areas We Cover</h3>
            {isPending ? (
              <div className="tp-skeleton flex flex-wrap gap-2" aria-hidden>
                <Skel className="h-6 w-16 tp-skel-round" />
                <Skel className="h-6 w-20 tp-skel-round" />
              </div>
            ) : companyInfo.service_areas.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {companyInfo.service_areas.map((area) => (
                  <span key={area} className="tp-footer-chip rounded-full px-3 py-1 text-xs">{area}</span>
                ))}
              </div>
            ) : (
              <p className="tp-footer-muted text-sm">Coming soon</p>
            )}
          </div>

          {/* Contact */}
          <div className="col-span-2 md:col-span-1 lg:col-span-3">
            <h3 className="tp-footer-heading mb-5">Contact Us</h3>
            <ul className="space-y-3.5">
              {companyInfo.phone_number && (
                <li className="flex items-center gap-3 text-[0.95rem]">
                  <Phone className="h-4 w-4 shrink-0 opacity-60" />
                  <a href={`tel:${companyInfo.phone_number.replace(/\s/g, '')}`} className="tp-footer-link">{companyInfo.phone_number}</a>
                </li>
              )}
              <li className="flex items-center gap-3 text-[0.95rem]">
                <Mail className="h-4 w-4 shrink-0 opacity-60" />
                <Link to={link("/contact")} className="tp-footer-link">Send us a message</Link>
              </li>
            </ul>
            <div className="mt-7">{socials}</div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="tp-footer-rule mt-16 flex flex-col gap-4 border-t pt-8 md:mt-20 md:flex-row md:items-center md:justify-between">
          {copyright}
          {legalLinks}
        </div>

      </div>
    </footer>
  );
}
